import { Suspense, useEffect, useState } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import {
	Box,
	Button,
	Checkbox,
	Container,
	Divider,
	LinearProgress,
	Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { toast } from 'sonner';

import {
	claimQueries,
	downloadReportClaimFile,
	useMutateSubmitReport,
} from '@repo/api/claim/claims';
import { ReportStatus } from '@repo/api/shared/report.types';
import { useUser } from '@repo/auth';
import { Info } from '@repo/ui/Info';

import { Route as ClaimRoute } from '../_claim';
import { AcceptFlow, RejectFlow } from './-components/ReportFlows';

export const Route = createFileRoute('/_claims/claim/$claimId/_claim/report')({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('claim.report')) {
			throw redirect({
				to: ClaimRoute.to,
				params,
			});
		}
	},
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(claimQueries.id(params.claimId));
		void context.queryClient.ensureQueryData(
			claimQueries.reportStatus(params.claimId),
		);
	},
	component: function ClaimReportPage() {
		const { permissions } = useUser();
		const confirm = useConfirm();

		const { claimId } = Route.useParams();
		const [{ data: claim }, { data: reportStatus }] = useSuspenseQueries({
			queries: [claimQueries.id(claimId), claimQueries.reportStatus(claimId)],
		});

		const navigate = Route.useNavigate();

		const [status, setStatus] = useState<ReportStatus>(reportStatus.status);
		const [submitted, setSubmitted] = useState(
			status === 'Approved' || status === 'Rejected',
		);
		const [hasRejectReason, setHasRejectReason] = useState(
			status === 'Rejected',
		);

		const { mutate: submitReport, isPending: submitting } =
			useMutateSubmitReport(claimId);

		const [file, setFile] = useState<File | undefined>();
		const [fileError, setFileError] = useState<string | undefined>();

		useEffect(() => {
			if (status === 'Approved' && submitted) {
				downloadReportClaimFile(claimId)
					.then(setFile)
					.catch(() => {
						setFileError('Failed to generate report');
					});
			}
		}, [claimId, status, submitted]);

		useEffect(() => {
			if (reportStatus.status === 'NoReport') {
				void navigate({
					to: ClaimRoute.to,
				});
			}
		}, [reportStatus.status, navigate]);

		return (
			<Container
				maxWidth="md"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					marginLeft: 0,
					'& hr': {
						width: '500px',
						marginInline: 'auto',
					},
				}}
			>
				<Typography variant="h6" component="h2">
					MDDi Report
				</Typography>
				<Typography variant="subtitle2">BI Loss Confirmation</Typography>
				<Typography variant="body2">
					Please review the calculations under the &#39;BI Analysis&#39; tab,
					and tick the below box to confirm that you accept the contents of the
					report. If the contents are not acceptable, you will be able to reject
					the calculations and close the claim within MDDi.
				</Typography>
				<Box
					sx={{
						width: '500px',
						marginInline: 'auto',
						p: 1,
						display: 'flex',
						gap: 2,
						borderRadius: 2,
					}}
				>
					<Button
						variant="outlined"
						startIcon={<Checkbox checked={status === 'Approved'} />}
						onClick={() => {
							void confirm({
								title: 'Accept Report',
								description:
									'By Accepting the report, the file will be closed and no further revisions can be made. Do you wish to proceed with accepting the report?',
								confirmationText: 'Yes',
								cancellationText: 'No',
								allowClose: false,
							}).then(({ confirmed }) => {
								setStatus(confirmed ? 'Approved' : 'NotReviewed');
							});
						}}
						disabled={submitted || claim.stage === 'Closed'}
						sx={{ pr: 3 }}
					>
						Accept
					</Button>
					<Button
						variant="outlined"
						startIcon={<Checkbox checked={status === 'Rejected'} />}
						onClick={() => {
							setStatus(status === 'Rejected' ? 'NotReviewed' : 'Rejected');
						}}
						disabled={submitted || claim.stage === 'Closed'}
						sx={{ pr: 3 }}
					>
						Reject
					</Button>
					<Button
						variant="contained"
						color="button"
						loading={submitting && status === 'Approved'}
						disabled={
							status === 'NotReviewed' || submitted || claim.stage === 'Closed'
						}
						sx={{ ml: 'auto' }}
						onClick={() => {
							if (status === 'Approved') {
								if (!permissions.includes('claim.report.approve')) {
									toast.error(
										'You do not have permission to approve the report',
									);
									return;
								}
								submitReport(
									{ approve: true },
									{
										onSuccess: () => {
											setSubmitted(true);
										},
									},
								);
							} else {
								setSubmitted(true);
							}
						}}
					>
						Submit
					</Button>
				</Box>
				{!submitted && status === 'Approved' && (
					<Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
						<Info>
							<Typography variant="subtitle2">
								MDDi is solely a quantitative tool for business interruption
								calculations.
							</Typography>
							<Typography variant="body2">
								Confirmation that the application of any and all insurance and
								legal policy coverage considerations are correctly included and
								considered within MDDi’s output is at the responsibility of the
								Carrier.
							</Typography>
							<Typography variant="caption">
								By clicking ‘Submit’ you acknowledge and accept the above.
							</Typography>
						</Info>
					</Box>
				)}
				{submitted && (
					<Suspense fallback={<LinearProgress />}>
						<Divider variant="middle" />
						{status === 'Rejected' && (
							<RejectFlow
								claimId={claimId}
								hasRejectReason={hasRejectReason}
								onRejectReasonSelected={() => {
									setHasRejectReason(true);
								}}
							/>
						)}
						{status === 'Approved' && (
							<AcceptFlow file={file} fileError={fileError} />
						)}
					</Suspense>
				)}
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Claim', 'Report'),
});
